import { Link } from "react-router-dom";
import { useApi } from "../../lib/useApi";
import { Card, PageHeader, Spinner, EmptyState } from "../../components/ui";
import { StatusBadge } from "../../components/StatusBadge";
import { date } from "../../lib/format";
import { statusLabel } from "../../lib/format";
import type { CaseStatus } from "../../lib/types";

interface Dashboard {
  byStatus: Partial<Record<CaseStatus, number>>;
  totalOpen: number;
  awaitingPayment: number;
  documentsPendingReview: number;
  recentCases: { id: string; publicRef: string; status: CaseStatus; createdAt: number }[];
}

export function CrmDashboard() {
  const { data, loading } = useApi<Dashboard>("/api/crm/dashboard");
  if (loading) return <Spinner />;
  if (!data) return <EmptyState>No hay datos.</EmptyState>;
  const entries = Object.entries(data.byStatus) as [CaseStatus, number][];

  return (
    <>
      <PageHeader title="Panel de operaciones" subtitle="Estado del negocio en tiempo real" />
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
        <Stat label="Expedientes abiertos" value={data.totalOpen} />
        <Stat label="Pendientes de pago" value={data.awaitingPayment} tone="warn" />
        <Stat label="Docs por revisar" value={data.documentsPendingReview} tone="warn" />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <Card>
          <div className="border-b border-line px-5 py-3.5 text-sm font-semibold">Por estado</div>
          {entries.length === 0 ? (
            <div className="p-6 text-sm text-muted">Sin expedientes.</div>
          ) : (
            <ul className="divide-y divide-line">
              {entries.map(([s, n]) => (
                <li key={s} className="flex items-center justify-between px-5 py-2.5 text-sm">
                  <span>{statusLabel(s)}</span>
                  <span className="tnum font-medium">{n}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card>
          <div className="border-b border-line px-5 py-3.5 text-sm font-semibold">Expedientes recientes</div>
          <ul className="divide-y divide-line">
            {data.recentCases.map((c) => (
              <li key={c.id}>
                <Link to={`/cases/${c.id}`} className="flex items-center justify-between px-5 py-3 hover:bg-canvas">
                  <span className="font-mono text-sm">{c.publicRef}</span>
                  <span className="flex items-center gap-3">
                    <span className="text-xs text-muted">{date(c.createdAt)}</span>
                    <StatusBadge status={c.status} />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: "warn" }) {
  return (
    <Card className="p-4">
      <div className="text-xs font-medium uppercase tracking-wide text-muted">{label}</div>
      <div className={`mt-1 text-2xl font-semibold tnum ${tone === "warn" && value > 0 ? "text-warn" : ""}`}>{value}</div>
    </Card>
  );
}
